/**
 * Clockwise Utility Functions
 * Pure, reusable helpers — no React deps.
 * Uses date-fns v4 (already in package.json).
 */
import {
  getWeek,
  startOfWeek,
  endOfWeek,
  addWeeks,
  format,
  addDays,
  getISOWeekYear,
} from 'date-fns';

// ─── Week Utilities ────────────────────────────────────────────────────────────

/** Returns the current ISO-week metadata for the given date (defaults to today). */
export const getWeekDetails = (date = new Date()) => {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  const end   = endOfWeek(date,   { weekStartsOn: 1 });
  return {
    week:  getWeek(date, { weekStartsOn: 1 }),
    year:  getISOWeekYear(date),
    start: format(start, 'yyyy-MM-dd'),
    end:   format(end,   'yyyy-MM-dd'),
    label: `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`,
  };
};

/** Navigate weeks from the current reference date. */
export const shiftWeek = (date, delta) => addWeeks(date, delta);

/** Returns the ISO date string (yyyy-MM-dd) for a specific day-of-week in a given year+week. */
export const getDateOfDay = (year, week, dayIndex) => {
  // Build a date in the target week, then find its Monday
  const jan4 = new Date(year, 0, 4);              // Jan 4 is always in week 1
  const weekStart = startOfWeek(jan4, { weekStartsOn: 1 });
  const targetMonday = addWeeks(weekStart, week - 1);
  return format(addDays(targetMonday, dayIndex), 'yyyy-MM-dd');
};

export const DAY_NAMES  = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_SHORT  = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const DAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/** Returns current day index 0=Mon…6=Sun */
export const getTodayIndex = () => {
  const d = new Date().getDay(); // 0=Sun…6=Sat
  return d === 0 ? 6 : d - 1;
};

// ─── Hour Helpers ──────────────────────────────────────────────────────────────

export const formatHours = (h) => {
  const n = parseFloat(h);
  return isNaN(n) ? '—' : n % 1 === 0 ? `${n}h` : `${n.toFixed(2)}h`;
};

export const sumHours = (tasks) =>
  tasks.reduce((s, t) => s + parseFloat(t.hours || 0), 0);

export const dayTotal = (tasks, dayIndex) =>
  tasks.filter((t) => t.day === dayIndex).reduce((s, t) => s + parseFloat(t.hours || 0), 0);

/** Returns a color class/value based on daily hours */
export const hoursColor = (h) => {
  if (h >= 8) return '#1b5e20';   // deep green
  if (h >= 4) return '#e65100';   // amber
  if (h > 0)  return '#1565c0';   // blue
  return '#90a4ae';               // grey
};

export const hoursBg = (h) => {
  if (h >= 8) return '#e8f5e9';
  if (h >= 4) return '#fff3e0';
  if (h > 0)  return '#e3f2fd';
  return 'transparent';
};

/** Generate hour picker options in 15-min steps 0..9:45 */
export const generateHourOptions = () => {
  const opts = [{ label: '0h', value: 0 }];
  for (let h = 0; h < 10; h++) {
    for (let m = 0; m < 60; m += 15) {
      if (h === 0 && m === 0) continue;
      const value = h + m / 60;
      const label = m === 0 ? `${h}h` : `${h}:${String(m).padStart(2, '0')}`;
      opts.push({ label, value });
    }
  }
  return opts;
};

// ─── Task Grouping ─────────────────────────────────────────────────────────────

/**
 * Group a flat task array into a structure for rendering:
 * { [projectId]: { id, name, rows: [{ label, type, refId, hours: [7] }] } }
 */
export const groupTasksForGrid = (tasks, projects = []) => {
  const projectMap = {};

  tasks.forEach((task) => {
    const proj = projects.find((p) => p.id === task.project_id) || { id: task.project_id || 'unassigned', name: 'Unassigned' };
    const pid = proj.id;

    if (!projectMap[pid]) {
      projectMap[pid] = { id: pid, name: proj.name, rows: [] };
    }

    // Build a stable row key
    const rowKey = task.client_id
      ? `client-${task.client_id}-svc-${task.service_id}`
      : task.project_service_id
        ? `ps-${task.project_service_id}`
        : task.marabes_id
          ? `mar-${task.marabes_id}`
          : task.external_id
            ? `ext-${task.external_id}`
            : `unk-${task.id}`;

    let row = projectMap[pid].rows.find((r) => r.key === rowKey);
    if (!row) {
      const label = task.client_name || task.service_name || task.marabes_name || task.external_name || task.project_service_name || 'Entry';
      row = { key: rowKey, label, type: getTaskType(task), refId: getTaskRefId(task), hours: Array(7).fill(0), taskIds: Array(7).fill(null), descriptions: Array(7).fill('') };
      projectMap[pid].rows.push(row);
    }

    if (task.day >= 0 && task.day <= 6) {
      row.hours[task.day]        += parseFloat(task.hours || 0);
      row.taskIds[task.day]       = task.id;
      row.descriptions[task.day]  = task.description || '';
    }
  });

  return projectMap;
};

export const getTaskType = (task) => {
  if (task.client_id)          return 'client';
  if (task.project_service_id) return 'project_service';
  if (task.marabes_id)         return 'marabes';
  if (task.external_id)        return 'external';
  return 'unknown';
};

export const getTaskRefId = (task) =>
  task.client_id || task.project_service_id || task.marabes_id || task.external_id || null;

// ─── Token / Auth ──────────────────────────────────────────────────────────────

export const decodeToken = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

// ─── Excel Export (uses xlsx already in project) ──────────────────────────────
export const exportTimesheetToExcel = async (tasks, weekLabel, projects) => {
  const { utils, writeFile } = await import('xlsx');
  const rows = tasks.map((t) => ({
    Week:        weekLabel,
    Project:     projects.find((p) => p.id === t.project_id)?.name || 'N/A',
    Day:         DAY_NAMES[t.day] || 'N/A',
    Hours:       t.hours,
    Description: t.description || '',
  }));
  const ws = utils.json_to_sheet(rows);
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, 'Timesheet');
  writeFile(wb, `timesheet-${weekLabel}.xlsx`);
};

// ─── Validation ────────────────────────────────────────────────────────────────
export const validateTask = (task) => {
  const errs = [];
  if (task.hours < 0 || task.hours > 24)     errs.push('Hours must be between 0 and 24');
  if (task.day < 0 || task.day > 6)          errs.push('Invalid day');
  if (!task.week || !task.year)              errs.push('Week/year required');
  return { valid: errs.length === 0, errors: errs };
};
